/* eslint-disable no-console */
/* eslint-disable no-param-reassign */
import axios, { AxiosError, AxiosRequestConfig } from 'axios';

type GetAccessTokenFn = () => string | null;
type RefreshAccessTokenFn = (forceRefresh?: boolean) => Promise<string | null>;
type OnAuthFailureFn = () => void;

interface IApiAuthHandlers {
  getAccessToken?: GetAccessTokenFn;
  refreshAccessToken?: RefreshAccessTokenFn;
  onAuthFailure?: OnAuthFailureFn;
}

interface IRetriableRequestConfig extends AxiosRequestConfig {
  retry?: boolean;
}

const authHandlers: IApiAuthHandlers = {};

export function registerApiAuthHandlers(handlers: IApiAuthHandlers): void {
  authHandlers.getAccessToken = handlers.getAccessToken;
  authHandlers.refreshAccessToken = handlers.refreshAccessToken;
  authHandlers.onAuthFailure = handlers.onAuthFailure;
}

const api = axios.create({
  baseURL:
    process.env.REACT_APP_INTEGRA_API_URL || process.env.REACT_APP_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para adicionar os headers dinâmicos antes de cada requisição
function obterUsuarioSalvo() {
  const usuarioSalvo =
    localStorage.getItem('@DS850:user') || localStorage.getItem('user');

  if (!usuarioSalvo) return null;

  try {
    return JSON.parse(usuarioSalvo);
  } catch {
    return null;
  }
}

api.interceptors.request.use(async config => {
  try {
    if (authHandlers.refreshAccessToken) {
      await authHandlers.refreshAccessToken(false);
    }
  } catch {
    // Falha silenciosa ao tentar renovar token antes da requisição
  }

  const accessToken =
    authHandlers.getAccessToken?.() ||
    localStorage.getItem('access_token') ||
    localStorage.getItem('token') ||
    localStorage.getItem('auth_token');

  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  const usuario = obterUsuarioSalvo();

  const empresaId = usuario?.empresa_id || usuario?.empresa;
  const filialId = usuario?.filial_id || usuario?.filial;

  if (empresaId) {
    config.headers['X-Empresa-Id'] = empresaId;
  }

  if (filialId) {
    config.headers['X-Filial-Id'] = filialId;
  }

  return config;
});

api.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const status = error?.response?.status;
    const originalRequest = error.config as IRetriableRequestConfig | undefined;

    if (status === 401 && originalRequest && !originalRequest.retry) {
      originalRequest.retry = true;

      try {
        const refreshedToken = await authHandlers.refreshAccessToken?.(true);

        if (refreshedToken) {
          originalRequest.headers = {
            ...(originalRequest.headers || {}),
            Authorization: `Bearer ${refreshedToken}`,
          };

          return api(originalRequest);
        }
      } catch {
        // A falha de refresh segue para o fluxo central de encerramento da sessão.
      }

      authHandlers.onAuthFailure?.();
    }

    return Promise.reject(error);
  },
);

export default api;
